import React from 'react';
import { Box, Button, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import LinkIcon from '@mui/icons-material/Link';
import AddLinkIcon from '@mui/icons-material/AddLink';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { describeJoin, tableLabel } from './modelGraphData';

const ACCENT = 'var(--wb-accent)';
const TITLE = 'var(--wb-text-title)';
const TEXT = 'var(--wb-text)';
const MUTED = 'var(--wb-muted-75)';
const BORDER = 'var(--wb-border)';
const MONO = { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.78rem' };

const Row = ({ label, children, mono = false }) => (
  <Box sx={{ py: 1, borderBottom: `1px solid ${BORDER}` }}>
    <Typography sx={{ color: MUTED, fontSize: '0.75rem', mb: 0.25 }}>{label}</Typography>
    <Typography component="div" sx={{ color: TEXT, fontSize: '0.85rem', wordBreak: 'break-word', ...(mono ? MONO : {}) }}>
      {children || '—'}
    </Typography>
  </Box>
);

const outlinedSx = { textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: ACCENT };
const dangerSx = { textTransform: 'none', borderColor: 'var(--wb-x-border-f8717166, rgba(248, 113, 113, 0.4))', color: 'var(--wb-danger)' };

const SectionTitle = ({ children }) => (
  <Typography sx={{ color: MUTED, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.6, mt: 2.5, mb: 1 }}>
    {children}
  </Typography>
);

const JoinSummary = ({ join, otherTable, direction, onSelectTable, onSelectRelation }) => (
  <Box sx={{ py: 1, borderBottom: `1px solid ${BORDER}` }}>
    <Stack direction="row" spacing={1} alignItems="center">
      <LinkIcon sx={{ fontSize: 16, color: ACCENT }} />
      <Typography
        component={otherTable && onSelectTable ? 'button' : 'span'}
        onClick={otherTable && onSelectTable ? () => onSelectTable(otherTable) : undefined}
        sx={{
          color: TEXT, fontSize: '0.85rem', background: 'none', border: 0, p: 0, textAlign: 'left',
          cursor: otherTable && onSelectTable ? 'pointer' : 'default',
          textDecoration: otherTable && onSelectTable ? 'underline dotted' : 'none'
        }}
      >
        {direction === 'out' ? '→ ' : '← '}
        {otherTable ? tableLabel(otherTable) : join.target_table}
      </Typography>
      <Typography sx={{ color: MUTED, fontSize: '0.75rem', flex: 1 }}>{describeJoin(join).cardinality}</Typography>
      {onSelectRelation && (
        // En pantallas táctiles una flecha delgada es difícil de tocar.
        <Button size="small" onClick={onSelectRelation} sx={{ textTransform: 'none', color: ACCENT, minWidth: 0, py: 0 }}>
          Ver relación
        </Button>
      )}
    </Stack>
    {(join.on || []).map((pair) => (
      <Typography key={`${pair.source_column}-${pair.target_column}`} sx={{ color: MUTED, pl: 3, ...MONO }}>
        {direction === 'out'
          ? `${pair.source_column} = ${pair.target_column}`
          : `${pair.target_column} = ${pair.source_column}`}
      </Typography>
    ))}
  </Box>
);

/**
 * Detalle de lo seleccionado en el diagrama: una tabla (columnas y joins
 * de entrada/salida) o un join (tablas, tipo, cardinalidad y columnas).
 */
const ModelGraphDetailsPanel = ({
  selection, edges, externalJoins, onClose, onSelectTable, onSelectEdge, onEditJoin, onDeleteJoin, onCreateJoinFrom,
  onEditTable
}) => {
  if (!selection) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: MUTED, fontSize: '0.9rem' }}>
          Haz clic en una tabla o en una relación del diagrama para ver su detalle.
        </Typography>
      </Box>
    );
  }

  const header = (kind, title, icon) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {icon}
          <Typography sx={{ color: MUTED, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>{kind}</Typography>
        </Stack>
        <Typography sx={{ color: TITLE, fontWeight: 600, fontSize: '1.05rem', wordBreak: 'break-word' }}>{title}</Typography>
      </Box>
      <Tooltip title="Cerrar detalle">
        <IconButton size="small" onClick={onClose} sx={{ color: MUTED }} aria-label="Cerrar detalle">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (selection.type === 'edge') {
    const { join, sourceTable, targetTable } = selection.data;
    const labels = describeJoin(join);
    return (
      <Box sx={{ p: 2.5 }}>
        {header('Relación', `${tableLabel(sourceTable)} → ${tableLabel(targetTable)}`, <LinkIcon sx={{ fontSize: 16, color: ACCENT }} />)}
        <Row label="Tabla origen">{tableLabel(sourceTable)}</Row>
        <Row label="Tabla destino">{tableLabel(targetTable)}</Row>
        <Row label="Tipo">{labels.type}</Row>
        <Row label="Cardinalidad">{labels.cardinality}</Row>
        <Row label="Columnas" mono>
          {(join.on || []).map((pair) => (
            <div key={`${pair.source_column}-${pair.target_column}`}>{`${pair.source_column} = ${pair.target_column}`}</div>
          ))}
        </Row>
        {join.purpose && <Row label="Propósito">{join.purpose}</Row>}
        {join.join_id ? (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
            {onEditJoin && (
              <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />} sx={outlinedSx} onClick={() => onEditJoin(selection.data)}>
                Editar
              </Button>
            )}
            {onDeleteJoin && (
              <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />} sx={dangerSx} onClick={() => onDeleteJoin(selection.data)}>
                Eliminar
              </Button>
            )}
          </Stack>
        ) : (
          <Typography sx={{ color: MUTED, fontSize: '0.75rem', mt: 2 }}>
            Esta relación no tiene identificador; edítala desde la entidad de la tabla origen.
          </Typography>
        )}
      </Box>
    );
  }

  const { table } = selection;
  const columns = table.columns || [];
  const outgoing = edges.filter((edge) => edge.data.sourceTable.table_id === table.table_id);
  const incoming = edges.filter((edge) => edge.data.targetTable.table_id === table.table_id);
  const external = externalJoins.filter((item) => item.sourceTableId === table.table_id);

  return (
    <Box sx={{ p: 2.5 }}>
      {header('Tabla', tableLabel(table), <TableChartOutlinedIcon sx={{ fontSize: 16, color: ACCENT }} />)}
      {table.description && <Typography sx={{ color: MUTED, fontSize: '0.85rem', mb: 1 }}>{table.description}</Typography>}
      <Row label="Nombre físico" mono>{table.table_name}</Row>
      <Row label="Base de datos / schema">{[table.database, table.schema].filter(Boolean).join(' / ')}</Row>

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {onEditTable && (
          <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />} sx={outlinedSx} onClick={() => onEditTable(table)}>
            Editar entidad
          </Button>
        )}
        {onCreateJoinFrom && (
          <Button variant="outlined" size="small" startIcon={<AddLinkIcon />} sx={outlinedSx} onClick={() => onCreateJoinFrom(table)}>
            Nueva relación desde esta tabla
          </Button>
        )}
      </Stack>

      <SectionTitle>Relaciones ({outgoing.length + incoming.length + external.length})</SectionTitle>
      {outgoing.length + incoming.length + external.length === 0 && (
        <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>Esta tabla no tiene joins definidos.</Typography>
      )}
      {outgoing.map((edge) => (
        <JoinSummary
          key={edge.id} join={edge.data.join} otherTable={edge.data.targetTable} direction="out"
          onSelectTable={onSelectTable} onSelectRelation={onSelectEdge && (() => onSelectEdge(edge))}
        />
      ))}
      {incoming.map((edge) => (
        <JoinSummary
          key={edge.id} join={edge.data.join} otherTable={edge.data.sourceTable} direction="in"
          onSelectTable={onSelectTable} onSelectRelation={onSelectEdge && (() => onSelectEdge(edge))}
        />
      ))}
      {external.map(({ join }, index) => (
        <Box key={`ext-${index}`}>
          <JoinSummary join={join} otherTable={null} direction="out" />
          <Typography sx={{ color: MUTED, fontSize: '0.72rem', pl: 3, pb: 1 }}>La tabla destino no forma parte de este modelo.</Typography>
        </Box>
      ))}

      <SectionTitle>Columnas ({columns.length})</SectionTitle>
      {columns.map((column) => (
        <Box key={column.column_name} sx={{ py: 0.75, borderBottom: `1px solid ${BORDER}` }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography sx={{ color: TEXT, ...MONO }}>{column.column_name}</Typography>
            <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>{column.data_type}</Typography>
            {column.semantic_type && (
              <Chip size="small" label={column.semantic_type} sx={{ height: 18, fontSize: '0.68rem', backgroundColor: 'var(--wb-accent-tint)', color: ACCENT }} />
            )}
          </Stack>
          {(column.business_name || column.description) && (
            <Typography sx={{ color: MUTED, fontSize: '0.75rem', mt: 0.25 }}>
              {[column.business_name, column.description].filter(Boolean).join(' — ')}
            </Typography>
          )}
        </Box>
      ))}
      <Divider sx={{ borderColor: 'transparent', mt: 1 }} />
    </Box>
  );
};

export default ModelGraphDetailsPanel;
